import { RoleLevel, Roles } from "../services/api/types/users/users";
import { User } from "../services/api/types/general/user";
import { DropdownPortal } from "../context/PortalsContext";

const ROLE_TO_LEVEL: Partial<Record<Roles, RoleLevel>> = {
  [Roles.SYSTEM_ADMIN]: RoleLevel.SYSTEM_ADMIN,
  [Roles.MAIN_ADMIN]: RoleLevel.MAIN_ADMIN,
  [Roles.PORTAL_ADMIN]: RoleLevel.PORTAL_ADMIN,
  [Roles.ADMIN]: RoleLevel.ADMIN,
  [Roles.FILE_HUB]: RoleLevel.MENU_ITEM,
  [Roles.REPORT_VIEWER]: RoleLevel.MENU_ITEM,
  [Roles.PORTAL_CONFIG]: RoleLevel.MENU_ITEM,
  [Roles.EMAIL_MNG]: RoleLevel.MENU_ITEM,
  [Roles.USER_MNG]: RoleLevel.MENU_ITEM,
  [Roles.USER_IMPORT_JOB]: RoleLevel.MENU_ITEM,
  [Roles.END_USER]: RoleLevel.END_USER,
};

export const getUserHighestRoleLevel = (roles: Roles[]): RoleLevel | null => {
  if (roles.length === 0) return null;
  const levels = roles.map((r) => ROLE_TO_LEVEL[r] ?? RoleLevel.END_USER);
  return Math.max(...levels) as RoleLevel;
};

export const ADMIN_ACCESS_ROLES = [
  Roles.MAIN_ADMIN,
  Roles.ADMIN,
  Roles.PORTAL_ADMIN,
  Roles.SYSTEM_ADMIN,
];

export const getEffectiveRoles = (
  user: User | null,
  _selectedPortal: DropdownPortal | null,
): Roles[] => {
  if (!user) return [];

  const userRoles = Array.isArray(user.roles) ? user.roles : [user.roles];
  return userRoles;
};

const hasAccess = (
  user: User | null,
  selectedPortal: DropdownPortal | null,
  allowedRoles: Roles[],
): boolean => {
  const effectiveRoles = getEffectiveRoles(user, selectedPortal);
  const rolesWithAdmins = [...ADMIN_ACCESS_ROLES, ...allowedRoles];
  return rolesWithAdmins.some((role) => effectiveRoles.includes(role));
};

export const hasAdminAccess = (
  user: User | null,
  selectedPortal: DropdownPortal | null,
): boolean => {
  // Admin-only — no extra specialized roles
  return hasAccess(user, selectedPortal, []);
};

export const hasPortalConfigurationAccess = (
  user: User | null,
  selectedPortal: DropdownPortal | null,
): boolean => {
  return hasAccess(user, selectedPortal, [
    Roles.REPORT_VIEWER,
    Roles.FILE_HUB,
    Roles.EMAIL_MNG,
    Roles.USER_MNG,
    Roles.USER_IMPORT_JOB,
    Roles.PORTAL_CONFIG,
    Roles.ASSIGNMENT_MNG,
  ]);
};

export const hasAnalyticsAccess = (
  user: User | null,
  selectedPortal: DropdownPortal | null,
): boolean => {
  return hasAccess(user, selectedPortal, [Roles.REPORT_VIEWER]);
};

export const hasFileHubAccess = (
  user: User | null,
  selectedPortal: DropdownPortal | null,
): boolean => {
  return hasAccess(user, selectedPortal, [Roles.FILE_HUB]);
};

export const hasEmailManagementAccess = (
  user: User | null,
  selectedPortal: DropdownPortal | null,
): boolean => {
  return hasAccess(user, selectedPortal, [Roles.EMAIL_MNG]);
};

export const hasUserManagementAccess = (
  user: User | null,
  selectedPortal: DropdownPortal | null,
): boolean => {
  return hasAccess(user, selectedPortal, [Roles.USER_MNG]);
};

export const hasUserImportJobAccess = (
  user: User | null,
  selectedPortal: DropdownPortal | null,
): boolean => {
  return hasAccess(user, selectedPortal, [Roles.USER_IMPORT_JOB]);
};

export const hasPortalConfigAccess = (
  user: User | null,
  selectedPortal: DropdownPortal | null,
): boolean => {
  return hasAccess(user, selectedPortal, [Roles.PORTAL_CONFIG]);
};

export const hasAssignmentManagementAccess = (
  user: User | null,
  selectedPortal: DropdownPortal | null,
): boolean => {
  return hasAccess(user, selectedPortal, [Roles.ASSIGNMENT_MNG]);
};

export const getFirstAccessiblePortalConfigPath = (
  user: User | null,
  selectedPortal: DropdownPortal | null,
  portalUid: string,
): string | null => {
  const base = `/portal-configuration/${portalUid}`;
  if (
    hasAdminAccess(user, selectedPortal) ||
    hasPortalConfigAccess(user, selectedPortal)
  )
    return `${base}/portal-settings`;
  if (hasAnalyticsAccess(user, selectedPortal)) return `${base}/dashboard`;
  if (hasFileHubAccess(user, selectedPortal)) return `${base}/file-hub`;
  if (hasEmailManagementAccess(user, selectedPortal))
    return `${base}/email-templates`;
  if (hasUserManagementAccess(user, selectedPortal)) return `${base}/users`;
  if (hasUserImportJobAccess(user, selectedPortal))
    return `${base}/bulk-user-actions`;
  if (hasAssignmentManagementAccess(user, selectedPortal))
    return `${base}/assignment-search`;
  return null;
};

export const hasAnyRole = (
  user: User | null,
  selectedPortal: DropdownPortal | null,
  roles: Roles[],
): boolean => {
  const effectiveRoles = getEffectiveRoles(user, selectedPortal);
  return roles.some((role) => effectiveRoles.includes(role));
};

/**
 * All roles in the same high→low order as max access level in portal configuration
 * (6 Super Admin through 1 End User; level-2 / menu item roles in product order).
 */
export const ASSIGNABLE_ROLES_PORTAL_CONFIG_ORDER: Roles[] = [
  Roles.SYSTEM_ADMIN,
  Roles.MAIN_ADMIN,
  Roles.PORTAL_ADMIN,
  Roles.ADMIN,
  Roles.FILE_HUB,
  Roles.REPORT_VIEWER,
  Roles.PORTAL_CONFIG,
  Roles.EMAIL_MNG,
  Roles.USER_MNG,
  Roles.USER_IMPORT_JOB,
  Roles.END_USER,
];

const PORTAL_CONFIG_ROLE_ORDER_INDEX: Partial<Record<Roles, number>> =
  Object.fromEntries(
    ASSIGNABLE_ROLES_PORTAL_CONFIG_ORDER.map((r, i) => [r, i]),
  ) as Record<Roles, number>;

export const sortRolesByPortalConfigOrder = (list: Roles[]): Roles[] => {
  return [...list].sort(
    (a, b) =>
      (PORTAL_CONFIG_ROLE_ORDER_INDEX[a] ?? 999) -
      (PORTAL_CONFIG_ROLE_ORDER_INDEX[b] ?? 999),
  );
};

/** Roles the user has that are not in the current assignable set (hidden from dropdown; chips not removable). */
export const getRolesNotInAssignableSet = (
  current: Roles[],
  assignable: Roles[],
): Roles[] => current.filter((r) => !assignable.includes(r));
const ROLE_LEVEL_LABEL: Record<RoleLevel, string> = {
  [RoleLevel.END_USER]: Roles.END_USER,
  [RoleLevel.MENU_ITEM]:
    "File Hub, Report Viewer, Portal Config, Email Management, User Management, User Import Job",
  [RoleLevel.ADMIN]: Roles.ADMIN,
  [RoleLevel.PORTAL_ADMIN]: Roles.PORTAL_ADMIN,
  [RoleLevel.MAIN_ADMIN]: Roles.MAIN_ADMIN,
  [RoleLevel.SYSTEM_ADMIN]: Roles.SYSTEM_ADMIN,
};

/** Highest role level first (for “maximum access” selects). */
export const ROLE_LEVEL_OPTIONS_DESC: RoleLevel[] = [
  RoleLevel.SYSTEM_ADMIN,
  RoleLevel.MAIN_ADMIN,
  RoleLevel.PORTAL_ADMIN,
  RoleLevel.ADMIN,
  RoleLevel.MENU_ITEM,
  RoleLevel.END_USER,
];

export const formatRoleLevelSelectLabel = (level: RoleLevel): string =>
  `${level} - ${ROLE_LEVEL_LABEL[level]}`;
